import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  BrpClassification as BrpClassificationType,
  BrpConfidence,
  BrpVerdict,
} from '../../helpers/types';

type ChipColor = 'error' | 'warning' | 'success' | 'info' | 'default';

export const BRP_VERDICT_LABELS: Record<BrpVerdict, string> = {
  threat: 'Threat',
  suspicious: 'Suspicious',
  benign: 'Benign',
  customer_owned: 'Customer Owned',
  insufficient_data: 'Insufficient Data',
};

const BRP_VERDICT_COLORS: Record<BrpVerdict, ChipColor> = {
  threat: 'error',
  suspicious: 'warning',
  benign: 'success',
  customer_owned: 'info',
  insufficient_data: 'default',
};

export const formatBrpScore = (score?: number | null): string | null =>
  typeof score === 'number' ? score.toFixed(2) : null;

export const formatBrpConfidence = (
  confidence?: BrpConfidence | null,
): string | null =>
  confidence ? confidence.charAt(0).toUpperCase() + confidence.slice(1) : null;

export const BrpVerdictChip = ({
  verdict,
}: {
  verdict?: BrpVerdict | null;
}) => {
  if (!verdict) return null;
  return (
    <Chip
      size="small"
      label={BRP_VERDICT_LABELS[verdict] ?? verdict}
      color={BRP_VERDICT_COLORS[verdict] ?? 'default'}
      data-testid={`brp-verdict-${verdict}`}
      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
    />
  );
};

// Single-column cell: verdict chip + inline score, confidence caption below.
export const BrpClassificationCell = ({
  classification,
}: {
  classification?: BrpClassificationType | null;
}) => {
  const verdict = classification?.verdict ?? null;
  const score = formatBrpScore(classification?.score);
  const confidence = formatBrpConfidence(classification?.confidence);

  if (!verdict && !score && !confidence) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="brp-classification-empty"
      >
        - Not classified
      </Typography>
    );
  }

  return (
    <Box data-testid="brp-classification-cell">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <BrpVerdictChip verdict={verdict} />
        {score && (
          <Typography variant="body2" data-testid="brp-score">
            {score}
          </Typography>
        )}
      </Box>
      {confidence && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="brp-confidence"
        >
          {confidence} confidence
        </Typography>
      )}
    </Box>
  );
};
